import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Pencil } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Spinner } from "@/components/ui/Spinner";
import {
  EmptyState, PageHeader, SectionCard, TableShell, Th,
} from "../../components/ui";
import { EditApplicationModal } from "../../components/wnioski/EditApplicationModal";
import { InfoHint } from "../../components/wnioski/InfoHint";
import { useApplicationRegistry, useApplications, useExpiringPoas, useWpwSequences } from "../../hooks";
import { APPLICATION_STATUS_COLORS, APPLICATION_STATUS_LABELS, type OfficialAppStatus } from "../../types";
import { fmtDate } from "../../utils";
import { useAuthStore } from "@/store/auth";
import { APPLICATION_APPROVAL_ENABLED } from "../../applicationApprovalFeature";

/**
 * Widok modułowy „Wnioski urzędowe”: wszystkie pakiety ze wszystkich projektów
 * (filtry typu/statusu) + alert wygasających pełnomocnictw. Pakiety zakłada się
 * z zakładki „Wnioski” konkretnego projektu.
 */
export function WnioskiHubPage() {
  const { data: registry } = useApplicationRegistry();
  // Pakiety WPW idą do negocjatora (nie do urzędu) i mają własny obieg w zakładce
  // WPW projektu — tu trzymamy je w osobnym zakresie, nie mieszając z urzędowymi.
  const wpwTypeKeys = useMemo(
    () => new Set((registry ?? []).filter((t) => t.recipient_mode === "negotiator").map((t) => t.key)),
    [registry],
  );
  // Zakres z URL (`?scope=wpw` — tak linkują kreator i zakładka WPW); przełącznik zapisuje go w URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope: "office" | "wpw" = searchParams.get("scope") === "wpw" ? "wpw" : "office";
  const setScope = (next: "office" | "wpw") =>
    setSearchParams((prev) => { const p = new URLSearchParams(prev); if (next === "wpw") p.set("scope", "wpw"); else p.delete("scope"); return p; }, { replace: true });
  // Szukanie po numerze (`?q=` — przetrwa wejście w wniosek i powrót). Numer wskazuje jeden
  // konkretny wniosek, więc szukanie pomija filtr statusu (typ / projekt / autor zostają).
  const numberQuery = searchParams.get("q") ?? "";
  const setNumberQuery = (next: string) =>
    setSearchParams((prev) => { const p = new URLSearchParams(prev); if (next) p.set("q", next); else p.delete("q"); return p; }, { replace: true });
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"" | "active" | OfficialAppStatus>("active");
  const [authorFilter, setAuthorFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  // „pokaż nieaktualnych właścicieli” — pakiety WPW właścicieli, których ponownie
  // zamówiony wypis nie potwierdził (backend chowa je domyślnie; zostają jako historia).
  const [showOutdated, setShowOutdated] = useState(false);
  const { data: applications, isLoading } = useApplications({
    ...(typeFilter ? { application_type: typeFilter } : {}),
    ...(showOutdated ? { include_outdated: "1" } : {}),
  });
  const { data: expiring } = useExpiringPoas();
  // Edycja wniosku wprost z listy (autor / dyrektor / CEO — backendowe can_edit).
  const [editId, setEditId] = useState<string | null>(null);

  // Typy pasujące do wybranego zakresu (dropdown „typ" pokazuje tylko je).
  const scopeTypes = useMemo(
    () => (registry ?? []).filter((t) => (scope === "wpw") === wpwTypeKeys.has(t.key)),
    [registry, scope, wpwTypeKeys],
  );

  // Wiersze bieżącego zakresu (przed filtrami autor/projekt/status) — z nich budujemy
  // listy wyboru dla dropdownów autor/projekt, żeby pokazywać tylko realnie obecne wartości.
  const scopeRows = useMemo(
    () => (applications ?? []).filter((a) => wpwTypeKeys.has(a.application_type) === (scope === "wpw")),
    [applications, scope, wpwTypeKeys],
  );
  const authorOptions = useMemo(
    () => [...new Set(scopeRows.map((a) => a.created_by_name).filter(Boolean))].sort(),
    [scopeRows],
  );
  const projectOptions = useMemo(
    () => [...new Set(scopeRows.map((a) => a.project_name).filter(Boolean))].sort(),
    [scopeRows],
  );
  // Kolejka „do weryfikacji": wnioski przekazane do akceptacji dyrektora (obieg 2026-09-25).
  const currentUser = useAuthStore((st) => st.user);
  const pendingApprovalCount = useMemo(
    () => scopeRows.filter((a) => a.status === "pending_approval").length,
    [scopeRows],
  );

  // „Sekwencje masowe WPW w toku" — realne, trwałe szkice kreatora (encja WpwMassSequence,
  // zapisywana na backendzie: wybrane działki z kroku 1 + krok). Wejście wprost z powrotem
  // do kreatora danego projektu. Pokazujemy tylko w zakresie „Pakiety WPW".
  const { data: seqData } = useWpwSequences(scope === "wpw");
  const sequences = seqData?.sequences ?? [];
  const finalized = seqData?.finalized ?? [];

  // Numer bez spacji i wielkości liter: „9006”, „wnu/2026/9006” i „WNU/2026/9006” trafiają tak samo.
  const needle = numberQuery.replace(/\s+/g, "").toLowerCase();

  const rows = useMemo(() => {
    let out = scopeRows;
    if (needle) out = out.filter((a) => a.number.toLowerCase().includes(needle));
    else if (statusFilter === "active")
      // „Aktywne" pomija zakończone/anulowane oraz nieaktywne szkice WPW (zastąpione nowszym).
      out = out.filter((a) => !["completed", "cancelled"].includes(a.status) && !a.superseded);
    else if (statusFilter) out = out.filter((a) => a.status === statusFilter);
    if (authorFilter) out = out.filter((a) => a.created_by_name === authorFilter);
    if (projectFilter) out = out.filter((a) => a.project_name === projectFilter);
    return out;
  }, [scopeRows, needle, statusFilter, authorFilter, projectFilter]);
  // Numer z drugiego zakresu (wniosek urzędowy ↔ pakiet WPW mają wspólną numerację WNU).
  const otherScopeHits = useMemo(
    () => (needle
      ? (applications ?? []).filter(
        (a) => wpwTypeKeys.has(a.application_type) !== (scope === "wpw")
          && a.number.toLowerCase().includes(needle),
      ).length
      : 0),
    [applications, needle, scope, wpwTypeKeys],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={scope === "wpw" ? "Pakiety WPW" : "Wnioski urzędowe"}
        subtitle={
          scope === "wpw"
            ? "Pakiety WPW (zgody prywatne, per działka) wszystkich projektów — obieg prowadzisz w zakładce WPW projektu"
            : "Pakiety wniosków urzędowych wszystkich projektów — lokalizacyjne, klauzule, odrolnienia, ULICP"
        }
      />

      {/* Rozdział: wnioski urzędowe (do urzędu) vs pakiety WPW (do negocjatora). */}
      <div className="inline-flex border border-[var(--color-border)] text-xs font-mono uppercase tracking-wider">
        {([["office", "Wnioski urzędowe"], ["wpw", "Pakiety WPW"]] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => { setScope(key); setTypeFilter(""); setAuthorFilter(""); setProjectFilter(""); }}
            className={`px-3 py-1.5 ${
              scope === key
                ? "bg-[var(--color-primary)] text-white"
                : "text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Kolejka „do weryfikacji" dyrektora (obieg akceptacji 2026-09-25): wnioski
          przekazane przez koordynatorów; klik otwiera widok kolejki (zatwierdź / zwróć
          wprost z listy). Obieg wyłączony (APPLICATION_APPROVAL_ENABLED) — baneru nie ma. */}
      {APPLICATION_APPROVAL_ENABLED && scope === "office" && pendingApprovalCount > 0
        && (currentUser?.is_ceo || currentUser?.role === "director") && (
        <Link
          to="/power-projects/wnioski/do-akceptacji"
          className="block w-full text-left text-xs border border-purple-300 bg-purple-50 text-purple-900 px-3 py-2 hover:bg-purple-100"
        >
          ⏳ {pendingApprovalCount} wniosk
          {pendingApprovalCount === 1
            ? "ek czeka"
            : pendingApprovalCount % 10 >= 2 && pendingApprovalCount % 10 <= 4
                && (pendingApprovalCount % 100 < 12 || pendingApprovalCount % 100 > 14)
              ? "i czekają"
              : "ów czeka"} na Twoją akceptację — otwórz kolejkę „Wnioski do akceptacji" →
        </Link>
      )}
      {/* Obieg wyłączony, ale wnioski przekazane do akceptacji PRZED wyłączeniem nadal
          czekają — bez kolejki i pozycji w menu byłyby do wyłowienia tylko wzrokiem.
          Każdy z dostępem je zatwierdzi i wyśle; do szkicu wycofa je tylko przekazujący
          albo autor (strona wniosku). */}
      {!APPLICATION_APPROVAL_ENABLED && scope === "office" && pendingApprovalCount > 0 && (
        <button
          type="button"
          onClick={() => setStatusFilter("pending_approval")}
          className="block w-full text-left text-xs border border-purple-300 bg-purple-50 text-purple-900 px-3 py-2 hover:bg-purple-100"
        >
          ⏳ Wnioski przekazane do akceptacji przed wyłączeniem obiegu: {pendingApprovalCount} — zatwierdź
          i wyślij je na stronie wniosku (do szkicu wycofa je przekazujący albo autor). Pokaż je →
        </button>
      )}

      {!!expiring?.length && (
        <div className="text-xs border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
          ⚠ {expiring.length} pełnomocnictw(a) wygasa lub wygasło —{" "}
          <Link to="/power-projects/pelnomocnictwa" className="underline">rejestr pełnomocnictw →</Link>
        </div>
      )}

      {scope === "wpw" && !!sequences.length && (
        <SectionCard
          title={
            <span className="flex items-center gap-1">
              Sekwencje masowe WPW w toku
              <InfoHint text="Sekwencja masowa jest prowadzona per projekt (zapisany szkic kreatora albo — bez szkicu — projekt z draftami WNU). Projekt ze zgłoszonym zapotrzebowaniem WPW czeka na jego akceptację; zlecenie domyka sekwencję automatycznie. Sekwencja zakończona bez zamówienia (krok 5) ma zapisane pakiety i czeka tu na zlecenie albo wniosek o zmianę istniejącego zamówienia. Kliknij, by wrócić do kreatora i dokończyć." />
            </span>
          }
        >
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {sequences.map((s) => {
              // Zakończona bez zamówienia: wejście prosto w krok 5 (lista zamówień do
              // wniosku o zmianę / „Nowe zamówienie"), nie na początek kreatora.
              const completed = s.status === "completed";
              return (
              <Link key={s.id}
                    to={s.project_id
                      ? `/power-projects/${s.project_id}/wpw-sekwencja${completed ? "?krok=zlecenie" : ""}`
                      : "#"}
                    className="block border border-[var(--color-border)] px-3 py-2 hover:bg-[var(--color-bg-elevated)] hover:border-[var(--color-primary)] transition-colors"
                    data-testid={`wpw-sequence-${s.id}`}>
                <div className="font-medium truncate">{s.project_name || "—"}</div>
                <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  {s.has_sequence
                    ? `${s.selected_count} zaznaczonych działek`
                    : `${s.wnu_draft_count} szkiców WNU (bez zapisanego postępu kreatora)`}
                  {s.has_sequence && s.wnu_draft_count ? ` · ${s.wnu_draft_count} szkiców WNU` : ""}
                  {completed ? " — zamów albo dołącz do zamówienia →" : " — wróć do kreatora →"}
                </div>
                {completed && (
                  <div className="text-[10px] text-emerald-700 dark:text-emerald-400 mt-0.5">
                    ✔ zakończona bez zamówienia{s.completed_at ? ` ${fmtDate(s.completed_at)}` : ""} — pakiety zapisane, do zlecenia
                  </div>
                )}
                {!!s.pending_requisitions?.length && (
                  <div className="text-[10px] text-amber-700 dark:text-amber-400 mt-0.5">
                    ⏳ czeka na akceptację zapotrzebowania WPW ({s.pending_requisitions.length}) — zlecenie domknie sekwencję
                  </div>
                )}
                <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
                  {s.author ? `${s.author} · ` : ""}
                  {s.updated_at ? `zmieniono ${fmtDate(s.updated_at)}` : "sekwencja w toku"}
                </div>
              </Link>
              );
            })}
          </div>
        </SectionCard>
      )}

      <SectionCard
        title={
          <span className="flex items-center gap-1">
            Pakiety
            <InfoHint text="Wnioski zakłada się z zakładki „Wnioski” na stronie projektu (tam jest panel „do obsłużenia” i rejestr decyzji projektu). Tutaj masz przegląd całości: filtruj po typie i statusie; kolumna faz pokazuje postęp kreatora." />
          </span>
        }
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="search"
              className="input-base !py-0.5 text-xs w-44"
              placeholder="Szukaj po numerze…"
              aria-label="Szukaj po numerze wniosku"
              title="Np. 9006 albo WNU/2026/9006 — szuka we wszystkich statusach"
              value={numberQuery}
              onChange={(e) => setNumberQuery(e.target.value)}
            />
            <select className="input-base !py-0.5 text-xs" value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">Wszystkie typy</option>
              {scopeTypes.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            <select className="input-base !py-0.5 text-xs" value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}>
              <option value="">Wszystkie projekty</option>
              {projectOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {scope === "wpw" && (
              <label
                className="flex cursor-pointer select-none items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]"
                title="Pakiety WPW właścicieli oznaczonych jako nieaktualni po ponownie zamówionym wypisie — zostają jako historia, domyślnie schowane."
              >
                <input
                  type="checkbox"
                  className="accent-[var(--color-primary)] cursor-pointer"
                  checked={showOutdated}
                  onChange={(e) => setShowOutdated(e.target.checked)}
                />
                pokaż nieaktualnych właścicieli
              </label>
            )}
            <select className="input-base !py-0.5 text-xs" value={authorFilter}
                    onChange={(e) => setAuthorFilter(e.target.value)}>
              <option value="">Wszyscy autorzy</option>
              {authorOptions.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <select className="input-base !py-0.5 text-xs" value={statusFilter}
                    disabled={!!needle}
                    title={needle ? "Szukanie po numerze obejmuje wszystkie statusy" : undefined}
                    onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
              <option value="active">Aktywne</option>
              <option value="">Wszystkie</option>
              {Object.entries(APPLICATION_STATUS_LABELS)
                // „Do akceptacji dyrektora" przy włączonym obiegu — po wyłączeniu tylko,
                // dopóki czekają wnioski przekazane wcześniej (i gdy ten filtr jest wybrany).
                .filter(([value]) =>
                  APPLICATION_APPROVAL_ENABLED || value !== "pending_approval"
                  || pendingApprovalCount > 0 || statusFilter === "pending_approval")
                .map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
            </select>
          </div>
        }
      >
        {isLoading ? (
          <div className="py-8 flex justify-center"><Spinner /></div>
        ) : !rows.length ? (
          <EmptyState>
            {needle
              ? `Brak ${scope === "wpw" ? "pakietów WPW" : "wniosków urzędowych"} o numerze zawierającym „${numberQuery.trim()}”`
              : scope === "wpw" ? "Brak pakietów WPW" : "Brak wniosków urzędowych"}
            {otherScopeHits > 0 && (
              <div className="mt-2">
                <button
                  type="button"
                  className="text-[var(--color-primary)] hover:underline"
                  onClick={() => { setScope(scope === "wpw" ? "office" : "wpw"); setTypeFilter(""); setAuthorFilter(""); setProjectFilter(""); }}
                >
                  Pasuje {otherScopeHits} w zakresie „{scope === "wpw" ? "Wnioski urzędowe" : "Pakiety WPW"}” — przełącz →
                </button>
              </div>
            )}
          </EmptyState>
        ) : (
          <TableShell>
            <thead>
              <tr>
                <Th>Numer</Th><Th>Typ</Th><Th>Projekt</Th><Th>Adresat</Th><Th>Fazy</Th><Th>Status</Th><Th>Utworzono</Th><Th> </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}
                    className={`border-t border-[var(--color-border)] hover:bg-[var(--color-bg-elevated)] ${
                      a.superseded || a.wpw_owner_outdated ? "opacity-50" : ""
                    }`}>
                  <td className="px-2 py-1.5 font-mono">
                    <Link to={`/power-projects/wnioski/${a.id}`} className="hover:underline font-bold">
                      {a.number}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">{a.type_label}</td>
                  <td className="px-2 py-1.5">
                    {a.project_id ? (
                      <Link to={`/power-projects/${a.project_id}?tab=wnioski`} className="hover:underline">
                        {a.project_name}
                      </Link>
                    ) : (
                      a.project_name
                    )}
                  </td>
                  <td className="px-2 py-1.5">{a.recipient_name || "—"}</td>
                  <td className="px-2 py-1.5 font-mono">{a.phase_progress.approved}/{a.phase_progress.total}</td>
                  <td className="px-2 py-1.5">
                    {a.superseded ? (
                      <Badge color="gray">nieaktywny (zastąpiony)</Badge>
                    ) : (
                      <Badge color={APPLICATION_STATUS_COLORS[a.status]}>{APPLICATION_STATUS_LABELS[a.status]}</Badge>
                    )}
                    {a.wpw_owner_outdated && (
                      <span className="ml-1" title={a.wpw_owner_outdated_reason || "Właściciel nieaktualny — ponowny wypis go nie potwierdził."}>
                        <Badge color="gray">właściciel nieaktualny</Badge>
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">{fmtDate(a.created_at)}</td>
                  <td className="px-2 py-1.5">
                    {a.can_edit && !a.superseded && (
                      <button
                        type="button"
                        className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        title="Edytuj wniosek (zakres, adresat, uwagi)"
                        onClick={() => setEditId(a.id)}
                      >
                        <Pencil size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
      </SectionCard>

      {editId && <EditApplicationModal applicationId={editId} onClose={() => setEditId(null)} />}

      {scope === "wpw" && !!finalized.length && (
        <SectionCard
          title={
            <span className="flex items-center gap-1">
              Sekwencje masowe WPW — zakończone
              <InfoHint text="Sekwencje domknięte utworzeniem zamówienia (krok 5). Kliknij, by otworzyć masową generację tego zlecenia — formularze, kompilacja, zwroty z terenu." />
            </span>
          }
        >
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {finalized.map((s) => (
              <Link key={s.id}
                    to={s.project_id && s.order_id ? `/power-projects/${s.project_id}/wpw-masowe/${s.order_id}` : "#"}
                    className="block border border-[var(--color-border)] px-3 py-2 hover:bg-[var(--color-bg-elevated)] hover:border-[var(--color-primary)] transition-colors">
                <div className="font-medium truncate">
                  <span className="text-[10px] font-mono uppercase text-green-700 dark:text-green-400 mr-1.5">✓ zakończona</span>
                  {s.project_name || "—"}
                </div>
                <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                  {s.order_number ? `zlecenie ${s.order_number}` : "zlecenie utworzone"}
                  {s.selected_count ? ` · ${s.selected_count} działek` : ""}
                  {s.order_id ? " — otwórz generację →" : ""}
                </div>
                <div className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
                  {s.author ? `${s.author} · ` : ""}
                  zakończono {fmtDate(s.finalized_at)}
                </div>
              </Link>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}
